import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import { Link as RouterLink, Outlet } from 'react-router-dom';

import { paths } from '@/routes/paths';
import { LanguageMenuButton } from '@/shared/components/language-menu-button';
import { Logo, LOGO_SIZE } from '@/shared/components/logo';
import { ThemeToggleButton } from '@/shared/components/theme-toggle-button';
import { useScrollOffset } from '@/shared/hooks/use-scroll-offset';

const HEADER_HEIGHT = { xs: 80, md: 96 };

// Centered shell for /auth/*. The header uses the same sticky-AppBar pattern as the
// landing-page header (transparent at top, frosted-blur + soft shadow once scrolled,
// Container + Toolbar layout). Trailing-edge controls differ: theme + language only,
// no gems/about/login nav (since this surface IS the login flow). Soft sage-tinted
// backdrop sits behind the centered content card.
export function AuthLayout() {
  const isOffset = useScrollOffset(16);
  const blurFilter = isOffset ? 'blur(20px)' : 'none';

  return (
    <Box
      sx={(theme) => ({
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.default,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          opacity: 0.24,
          backgroundImage: `radial-gradient(at 18% 12%, ${theme.palette.primary.light} 0px, transparent 55%), radial-gradient(at 86% 88%, ${theme.palette.primary.dark} 0px, transparent 55%)`,
          ...theme.applyStyles('dark', { opacity: 0.08 }),
        },
      })}
    >
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={(theme) => ({
          zIndex: theme.zIndex.appBar,
          backgroundColor: isOffset ? 'rgba(250, 250, 247, 0.8)' : 'transparent',
          backdropFilter: blurFilter,
          WebkitBackdropFilter: blurFilter,
          boxShadow: isOffset
            ? '0 1px 0 rgba(0, 0, 0, 0.06), 0 8px 24px -16px rgba(0, 0, 0, 0.12)'
            : 'none',
          transitionProperty: 'background-color, box-shadow, backdrop-filter',
          transitionDuration: `${theme.transitions.duration.shorter}ms`,
          transitionTimingFunction: theme.transitions.easing.easeInOut,
        })}
      >
        <Container maxWidth="lg" disableGutters sx={{ px: { xs: 2.5, md: 5 } }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: HEADER_HEIGHT,
              height: HEADER_HEIGHT,
              justifyContent: 'space-between',
            }}
          >
            <Box
              component={RouterLink}
              to={paths.home}
              aria-label="Sahib"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Logo size={LOGO_SIZE.primary} />
            </Box>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              <ThemeToggleButton />
              <LanguageMenuButton />
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        component="main"
        sx={{
          position: 'relative',
          zIndex: 2,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 5 },
        }}
      >
        <Box
          sx={(theme) => ({
            width: '100%',
            maxWidth: '420px',
            py: 5,
            px: 3,
            borderRadius: (t) => `${t.shape.borderRadius}px`,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.background.default,
          })}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
